// **Loading `waggle-wasm` at runtime** (`rounds-practice-sandbox` task 5.12).
//
// One small file, because the *how* of reaching generated build output from a bundled app is a real
// constraint with a real trade-off, and it is better stated once here than rediscovered.
//
// ## The constraint
//
// `client/src/generated/wasm/` is produced by `cargo xtask build-wasm-client` and is **gitignored**.
// `tsc --noEmit` and `vite build` must stay green on a fresh clone that has not run it — that is why
// `practice-trace-store.ts`'s `WasmChecksumModule` and `practice-runner.ts`'s
// `PracticeDocumentCompiler` are declared structurally instead of imported, and it is why nothing in
// `client/src/` may name a path under `generated/wasm/` in a way a bundler resolves. A static import
// fails the build outright; so does a dynamic `import('./generated/wasm/web/waggle_wasm.js')`, because
// Rollup resolves a literal specifier at build time exactly as it resolves a static one.
//
// ## The resolution
//
// The module is loaded from a **served URL** with `import(/* @vite-ignore */ url)`, so the bundler
// never resolves it and the fresh-clone build is untouched. `cargo xtask build-wasm-client` mirrors the
// `web` flavour into `client/public/wasm/`, which Vite serves at `/wasm/` in dev and copies into
// `dist/wasm/` on build — so the dev path and the packaged path are the *same URL*, which is the
// property the `/api`-prefix escalation recorded in `vite.config.ts` was about.
//
// Colocating the glue with its `.wasm` in one directory is load-bearing, not tidiness: the `web`
// glue's own initialiser defaults to `new URL('waggle_wasm_bg.wasm', import.meta.url)`, which resolves
// beside itself. So **no asset URL has to be threaded through the client at all** — the one place the
// two files' relationship is expressed is the directory they share.
//
// ## What a missing artifact does
//
// It refuses, and says so. A fresh clone that has not provisioned the module gets a 404 on the glue,
// which surfaces as a compile-gate refusal naming the reason (`unavailableEngineCompiler`) and a run
// that never starts — never as a fabricated "it compiles", which is the request `rounds-local-run`
// forbids sending.

import type { PracticeWasmModule } from './practice-engine';

/** Where the mirrored `web` glue is served from, relative to the app's base. Mirrors
 * `xtask::build_wasm_client::PUBLIC_MIRROR`. */
export const WASM_GLUE_PATH = 'wasm/waggle_wasm.js';

/**
 * The URL the glue is served from, honouring Vite's configured `base` so a deployment under a
 * sub-path resolves it too.
 *
 * Read defensively: `import.meta.env` is a Vite injection, and this function is also reached from a
 * worker and from Vitest, so a missing `BASE_URL` degrades to the root rather than throwing at load.
 */
export function practiceWasmGlueUrl(): string {
  let base = '/';
  try {
    const configured = (import.meta as { env?: { BASE_URL?: string } }).env?.BASE_URL;
    if (typeof configured === 'string' && configured.length > 0) {
      base = configured.endsWith('/') ? configured : `${configured}/`;
    }
  } catch {
    base = '/';
  }
  return `${base}${WASM_GLUE_PATH}`;
}

/** A `wasm-bindgen` glue namespace: the named exports plus the initialiser the `web` target adds. */
type GlueNamespace = Record<string, unknown> & {
  default?: () => Promise<unknown>;
};

/**
 * Loads and initialises the module.
 *
 * The `@vite-ignore` is the whole point — see the header. `default` is the `web` target's async
 * initialiser and is absent under the `nodejs` target (which instantiates synchronously at module
 * load), so it is called only when present and the same function serves both flavours.
 */
export async function loadPracticeWasmModule(
  url: string = practiceWasmGlueUrl(),
): Promise<PracticeWasmModule> {
  const glue = (await import(/* @vite-ignore */ url)) as GlueNamespace;
  if (typeof glue.default === 'function') {
    await glue.default();
  }
  if (typeof glue.practice_configure !== 'function') {
    throw new Error(`${url} is not a waggle-wasm module: practice_configure is missing`);
  }
  return glue as unknown as PracticeWasmModule;
}
